import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { apiDefaultFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  BehaviorLogApiPath,
  ListBehaviorLogsResponse,
  ListBehaviorLogsVars
} from '@/common/api/params/behaviorLog'
import {
  PageRankingsApiPath,
  PageRankingsResponse,
  PageRankingsVars
} from '@/common/api/params/pageRankings'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'
import { BehaviorActionType } from '@/common/model/recommend/behavior'

export interface BehaviorLogSearchCondition extends SearchConditionBase {
  keyword: string | null
  actionType: BehaviorActionType | null
  visitorId: string | null
  itemId: string | null
  categoryId: string | null
  customerId: string | null
  dateFrom: string | null
  dateTo: string | null
}

const behaviorLogSearchConditionParam: SearchConditionParam[] = [
  { name: 'keyword', type: 'string' },
  { name: 'actionType', type: 'string' },
  { name: 'visitorId', type: 'string' },
  { name: 'itemId', type: 'string' },
  { name: 'categoryId', type: 'string' },
  { name: 'customerId', type: 'string' },
  { name: 'dateFrom', type: 'string' },
  { name: 'dateTo', type: 'string' }
]

export const BEHAVIOR_LOG_SEARCH_CONDITION_CLEAR: BehaviorLogSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  actionType: null,
  visitorId: null,
  itemId: null,
  categoryId: null,
  customerId: null,
  dateFrom: null,
  dateTo: null
}

export const useBehaviorLogSearchCondition = (): [
  BehaviorLogSearchCondition,
  Dispatch<SetStateAction<BehaviorLogSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<BehaviorLogSearchCondition>(behaviorLogSearchConditionParam)
}

export const convertConditionToBehaviorLogsVars = (
  siteId: number,
  condition: BehaviorLogSearchCondition
): ListBehaviorLogsVars => {
  return {
    site_id: siteId,
    page: condition.page,
    per_page: condition.perPage,
    ...(condition.keyword && { search: condition.keyword }),
    ...(condition.actionType && { action_type: condition.actionType }),
    ...(condition.visitorId && { s_visitor_id: condition.visitorId }),
    ...(condition.itemId && { s_item_id: condition.itemId }),
    ...(condition.categoryId && { s_category_id: condition.categoryId }),
    ...(condition.customerId && { s_customer_id: condition.customerId }),
    ...(condition.dateFrom && { s_date_from: condition.dateFrom }),
    ...(condition.dateTo && { s_date_to: condition.dateTo })
  }
}

export const useBehaviorLogsApi = (vars: ListBehaviorLogsVars | null) => {
  return useSWR<ListBehaviorLogsResponse, Response, [string, ListBehaviorLogsVars] | null>(
    vars ? [BehaviorLogApiPath.LIST_BEHAVIOR_LOGS, vars] : null,
    apiDefaultFetcher
  )
}

export const usePageRankingsApi = (vars: PageRankingsVars | null) => {
  return useSWR<PageRankingsResponse, Response, [string, PageRankingsVars] | null>(
    vars ? [PageRankingsApiPath.PAGE_RANKINGS, vars] : null,
    apiDefaultFetcher
  )
}
